const CACHE_TTL_MS = 5 * 60 * 1000;
const SHA256_RE = /^[0-9a-f]{64}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PRODUCT_STATEMENT_MAX_LENGTH = 20_000;
const REASONS = new Set([
  'clinical_policy_manifest_missing',
  'clearance_policy_missing',
  'risk_profile_policy_incomplete',
  'clinical_policy_unsealed',
  'clinical_policy_expired',
  'clinical_policy_incomplete',
  'clinical_policy_stale',
  'clinical_policy_roster_stale',
  'clinical_policy_signature_invalid',
  'registry_definition_missing',
  'component_roster_missing',
  'component_catalog_missing',
  'component_payload_invalid',
  'component_roster_mismatch',
  'component_content_unvalidated',
  'component_confirmation_missing',
  'clearance_execution_spec_missing',
  'clinical_policy_not_loaded',
  'clinical_policy_preload_failed',
  'clinical_policy_response_invalid',
]);

const REASON_MESSAGES = Object.freeze({
  clinical_policy_manifest_missing: 'le manifeste clinique MGD est absent',
  clearance_policy_missing: 'la politique de clairance contient une décision à confirmer',
  risk_profile_policy_incomplete: 'le registre des profils à risque est incomplet',
  clinical_policy_unsealed: 'le registre clinique MGD n’est pas scellé par Anissa',
  clinical_policy_expired: 'le registre clinique MGD est périmé',
  clinical_policy_incomplete: 'un composant du registre clinique MGD est incomplet',
  clinical_policy_stale: 'les règles cliniques ont changé depuis le dernier scellement',
  clinical_policy_roster_stale: 'le roster clinique a changé depuis le dernier scellement',
  clinical_policy_signature_invalid: 'l’empreinte du manifeste clinique est invalide',
  registry_definition_missing: 'la définition du registre clinique est absente',
  component_roster_missing: 'un roster clinique requis est absent',
  component_catalog_missing: 'un catalogue clinique requis est vide',
  component_payload_invalid: 'un composant clinique est illisible',
  component_roster_mismatch: 'un composant ne correspond pas à son roster déclaré',
  component_content_unvalidated: 'un composant contient des décisions à confirmer',
  component_confirmation_missing: 'un composant attend une confirmation explicite',
  clearance_execution_spec_missing: 'une règle d’exécution clinique reste à structurer et confirmer',
  clinical_policy_not_loaded: 'le registre clinique MGD n’a pas été préchargé',
  clinical_policy_preload_failed: 'le registre clinique MGD est indisponible',
  clinical_policy_response_invalid: 'la preuve du registre clinique MGD est illisible',
});

let registryState = null;

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function immutableJson(value) {
  if (Array.isArray(value)) return Object.freeze(value.map(immutableJson));
  if (isPlainObject(value)) {
    return Object.freeze(Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, immutableJson(item)]),
    ));
  }
  return value;
}

function freezeRows(rows) {
  return Object.freeze(rows.map((row) => immutableJson(row)));
}

function normalizeReason(reason, defaultReason = 'clinical_policy_response_invalid') {
  return REASONS.has(reason) ? reason : defaultReason;
}

function exactObject(value, fields) {
  return isPlainObject(value)
    && Object.keys(value).length === fields.length
    && fields.every((field) => Object.hasOwn(value, field));
}

function validIsoTimestamp(value) {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

function unavailableProductAuthorization(reason = 'product_authorization_unavailable') {
  return Object.freeze({ ok: false, available: false, active: false, reason });
}

function normalizeProductAuthorization(data) {
  const fields = ['active', 'decidedAt', 'decision', 'statementHash', 'statementText',
    'statementVersion', 'version'];
  if (!exactObject(data, fields) || data.version !== 1 || typeof data.active !== 'boolean'
      || !Number.isInteger(data.statementVersion) || data.statementVersion < 1
      || !SHA256_RE.test(data.statementHash || '')
      || typeof data.statementText !== 'string' || !data.statementText.trim()
      || data.statementText.length > PRODUCT_STATEMENT_MAX_LENGTH
      || !(data.decidedAt === null || validIsoTimestamp(data.decidedAt))
      || ![null, 'authorized', 'revoked'].includes(data.decision)) return null;
  if ((data.active && data.decision !== 'authorized')
      || (!data.active && data.decision === 'authorized')
      || (data.decision !== null && !validIsoTimestamp(data.decidedAt))) return null;
  return Object.freeze({ ok: true, available: true, version: 1, active: data.active,
    decision: data.decision, statementVersion: data.statementVersion,
    statementHash: data.statementHash, statementText: data.statementText,
    decidedAt: data.decidedAt });
}

function normalizeManifest(data) {
  if (!isPlainObject(data) || typeof data.ready !== 'boolean') return null;
  const reason = data.ready ? null : normalizeReason(data.reason);
  const blockers = Array.isArray(data.blockers)
    ? freezeRows(data.blockers.filter((row) => isPlainObject(row)
      && typeof row.code === 'string' && typeof row.target_key === 'string'))
    : Object.freeze([]);
  const pendingConfirmations = Array.isArray(data.pending_confirmations)
    ? freezeRows(data.pending_confirmations.filter((row) => isPlainObject(row)
      && ['clearance_policy', 'component_roster'].includes(row.target_type)
      && typeof row.target_key === 'string' && row.target_key
      && isPlainObject(row.parameters) && SHA256_RE.test(row.content_sha256 || '')
      && isPlainObject(row.decision_provenance)))
    : Object.freeze([]);
  if (data.registry_key !== 'mgd_plan_generation' || data.locale !== 'fr'
      || !Number.isInteger(data.policy_version) || data.policy_version <= 0) {
    return null;
  }
  const registryDefinition = isPlainObject(data.registry_definition)
    ? data.registry_definition : {};
  const candidateKeys = Array.isArray(registryDefinition.required_components)
    ? registryDefinition.required_components : [];
  const componentKeys = candidateKeys.every((key) => typeof key === 'string' && key)
      && new Set(candidateKeys).size === candidateKeys.length
    ? candidateKeys : [];
  const componentHashes = isPlainObject(data.component_hashes) ? data.component_hashes : {};
  const componentCounts = isPlainObject(data.component_counts) ? data.component_counts : {};
  const componentRosters = isPlainObject(data.component_rosters) ? data.component_rosters : {};
  const rowsByComponent = {
    guardrails: Array.isArray(data.guardrail_policies) ? data.guardrail_policies : [],
    interactions: Array.isArray(data.interaction_policies) ? data.interaction_policies : [],
    maternal: Array.isArray(data.maternal_policies) ? data.maternal_policies : [],
    thresholds: Array.isArray(data.threshold_policies) ? data.threshold_policies : [],
    risk_profiles: Array.isArray(data.risk_profiles) ? data.risk_profiles : [],
    clearance: Array.isArray(data.clearance_policies) ? data.clearance_policies : [],
  };
  const fullShapeValid = SHA256_RE.test(data.manifest_sha256 || '')
    && SHA256_RE.test(data.registry_definition_sha256 || '')
    && componentKeys.length > 0
    && (data.valid_until === null || data.valid_until === undefined
      || (typeof data.valid_until === 'string' && Number.isFinite(Date.parse(data.valid_until))))
    && componentKeys.every((key) => SHA256_RE.test(componentHashes[key] || '')
      && Number.isInteger(componentCounts[key]) && componentCounts[key] >= 0
      && isPlainObject(componentRosters[key])
      && Array.isArray(rowsByComponent[key])
      && rowsByComponent[key].length === componentCounts[key]);
  let rosterRowsValid = fullShapeValid;
  if (rosterRowsValid) for (const key of componentKeys) {
    const roster = componentRosters[key];
    if (typeof roster.item_key_field !== 'string' || !roster.item_key_field
        || !Array.isArray(roster.required_item_keys) || roster.required_item_keys.length === 0) {
      rosterRowsValid = false;
      break;
    }
    const actualKeys = rowsByComponent[key]
      .map((row) => row?.[roster.item_key_field]).sort();
    const requiredKeys = [...roster.required_item_keys].sort();
    if (roster.enabled !== true || roster.decision_status !== 'validated_anissa'
        || new Set(requiredKeys).size !== requiredKeys.length
        || actualKeys.length !== requiredKeys.length
        || actualKeys.some((item, index) => item !== requiredKeys[index])
        || rowsByComponent[key].some((row) => !isPlainObject(row)
          || typeof row[roster.item_key_field] !== 'string' || !row[roster.item_key_field])) {
      rosterRowsValid = false;
      break;
    }
  }
  const riskProfiles = freezeRows(rowsByComponent.risk_profiles.filter(isPlainObject));
  const rowIsValidatedForReady = (row) => row.decision_status === 'validated_anissa'
    || (row.pending === false && ['accepted', 'corrected'].includes(row.decision));
  const riskRowsValid = !riskProfiles.some((row) => typeof row.flag_id !== 'string' || !row.flag_id
      || typeof row.label_fr !== 'string' || !row.label_fr.trim()
      || typeof row.mandatory_validation !== 'boolean' || (data.ready && row.enabled !== true)
      || (data.ready && !rowIsValidatedForReady(row)));
  const clearancePolicies = freezeRows(rowsByComponent.clearance.filter(isPlainObject));
  const clearanceRowsValid = !clearancePolicies.some((row) => typeof row.policy_key !== 'string' || !row.policy_key
      || !isPlainObject(row.parameters) || (data.ready && row.enabled !== true)
      || (data.ready && !rowIsValidatedForReady(row)));
  const componentRowsValid = Object.values(rowsByComponent).every((rows) => rows.every(isPlainObject))
    && !Object.values(rowsByComponent).flat().some((row) => row.enabled !== true)
    && !rowsByComponent.guardrails.some((row) => typeof row.profile_key !== 'string'
      || typeof row.display_name !== 'string' || !row.display_name.trim()
      || !isPlainObject(row.detection_rule) || Object.keys(row.detection_rule).length === 0
      || !Array.isArray(row.forbidden_phrases)
      || row.forbidden_phrases.length === 0)
    && !rowsByComponent.interactions.some((row) => typeof row.substance_key !== 'string'
      || !['blocking', 'advisory'].includes(row.classification)
      || typeof row.reason !== 'string' || !row.reason.trim()
      || !Array.isArray(row.escalate_to_blocking_if))
    && !rowsByComponent.maternal.some((row) => typeof row.state !== 'string'
      || typeof row.label_fr !== 'string' || !row.label_fr.trim()
      || !Array.isArray(row.rules_fr) || row.rules_fr.length === 0
      || typeof row.label_en !== 'string' || !row.label_en.trim()
      || !Array.isArray(row.rules_en) || row.rules_en.length === 0)
    && !rowsByComponent.thresholds.some((row) => typeof row.threshold_key !== 'string'
      || !Number.isFinite(Number(row.value))
      || typeof row.description !== 'string' || !row.description.trim());
  if (data.ready && (typeof data.sealed_at !== 'string'
      || !Number.isFinite(Date.parse(data.sealed_at))
      || registryDefinition.enabled !== true
      || registryDefinition.decision_status !== 'validated_anissa'
      || blockers.length > 0 || pendingConfirmations.length > 0
      || !rosterRowsValid || !riskRowsValid || !clearanceRowsValid
      || !componentRowsValid)) return null;
  return Object.freeze({
    ready: data.ready,
    reason,
    registryKey: data.registry_key,
    locale: data.locale,
    policyVersion: data.policy_version,
    manifestSha256: SHA256_RE.test(data.manifest_sha256 || '') ? data.manifest_sha256 : null,
    componentHashes: immutableJson(componentHashes),
    componentCounts: immutableJson(componentCounts),
    componentRosters: immutableJson(componentRosters),
    registryDefinition: immutableJson(registryDefinition),
    registryDefinitionSha256: SHA256_RE.test(data.registry_definition_sha256 || '')
      ? data.registry_definition_sha256 : null,
    sealedAt: typeof data.sealed_at === 'string' && Number.isFinite(Date.parse(data.sealed_at))
      ? data.sealed_at : null,
    validUntil: typeof data.valid_until === 'string' && Number.isFinite(Date.parse(data.valid_until))
      ? data.valid_until : null,
    riskProfiles,
    clearancePolicies,
    guardrailPolicies: freezeRows(rowsByComponent.guardrails.filter(isPlainObject)),
    interactionPolicies: freezeRows(rowsByComponent.interactions.filter(isPlainObject)),
    maternalPolicies: freezeRows(rowsByComponent.maternal.filter(isPlainObject)),
    thresholdPolicies: freezeRows(rowsByComponent.thresholds.filter(isPlainObject)),
    evaluatorPolicies: freezeRows(Array.isArray(data.evaluator_policies)
      ? data.evaluator_policies.filter(isPlainObject) : []),
    planContractPolicies: freezeRows(Array.isArray(data.plan_contract_policies)
      ? data.plan_contract_policies.filter(isPlainObject) : []),
    dataHandlingPolicies: freezeRows(Array.isArray(data.data_handling_policies)
      ? data.data_handling_policies.filter(isPlainObject) : []),
    blockers,
    pendingConfirmations,
  });
}

export class ClinicalPolicyRegistryError extends Error {
  constructor(reason = 'clinical_policy_response_invalid') {
    const safeReason = normalizeReason(reason);
    super(`À valider Anissa — ${REASON_MESSAGES[safeReason]}.`);
    this.name = 'ClinicalPolicyRegistryError';
    this.code = safeReason;
  }
}

/**
 * Charge la preuve calculée côté PostgreSQL. Il n'existe volontairement aucun
 * repli local : une panne ou une preuve incomplète bloque le chemin MGD.
 */
export async function preloadClinicalPolicyRegistryFromSupabase(supabaseClient, opts = {}) {
  const { force = false } = opts;
  if (!force && registryState && Date.now() - registryState.loadedAt < CACHE_TTL_MS) {
    return registryState.result;
  }
  if (!supabaseClient || typeof supabaseClient.rpc !== 'function') {
    const result = Object.freeze({ ok: false, ready: false, source: 'unavailable',
      reason: 'clinical_policy_preload_failed' });
    registryState = { loadedAt: Date.now(), manifest: null, result };
    return result;
  }
  try {
    const { data, error } = await supabaseClient.rpc('lire_manifeste_registre_clinique_mgd');
    if (error) {
      const result = Object.freeze({ ok: false, ready: false, source: 'supabase',
        reason: 'clinical_policy_preload_failed' });
      registryState = { loadedAt: Date.now(), manifest: null, result };
      return result;
    }
    if (isPlainObject(data) && data.ready === false
        && data.reason === 'clinical_policy_manifest_missing'
        && !Number.isInteger(data.policy_version)) {
      const result = Object.freeze({ ok: false, ready: false, source: 'supabase',
        reason: 'clinical_policy_manifest_missing', manifest: null });
      registryState = { loadedAt: Date.now(), manifest: null, result };
      return result;
    }
    const manifest = normalizeManifest(data);
    if (!manifest) {
      const result = Object.freeze({ ok: false, ready: false, source: 'supabase',
        reason: 'clinical_policy_response_invalid' });
      registryState = { loadedAt: Date.now(), manifest: null, result };
      return result;
    }
    const result = Object.freeze({ ok: manifest.ready, ready: manifest.ready, source: 'supabase',
      reason: manifest.reason, manifest, policyVersion: manifest.policyVersion,
      blockers: manifest.blockers,
      pendingConfirmations: manifest.pendingConfirmations });
    registryState = { loadedAt: Date.now(), manifest, result };
    return result;
  } catch {
    const result = Object.freeze({ ok: false, ready: false, source: 'supabase',
      reason: 'clinical_policy_preload_failed' });
    registryState = { loadedAt: Date.now(), manifest: null, result };
    return result;
  }
}

/** Renvoie le snapshot strict courant ou null. Aucun repli sur des constantes. */
export function getClinicalPolicyManifest() {
  if (!registryState || Date.now() - registryState.loadedAt >= CACHE_TTL_MS) return null;
  return registryState.manifest;
}

/** Barrière synchrone à appeler juste avant toute préparation/génération MGD. */
export function assertMgdClinicalPolicyReady(manifest = getClinicalPolicyManifest()) {
  if (!manifest) {
    const reason = registryState && Date.now() - registryState.loadedAt < CACHE_TTL_MS
      ? registryState.result.reason
      : 'clinical_policy_not_loaded';
    throw new ClinicalPolicyRegistryError(reason);
  }
  if (manifest.ready !== true) {
    throw new ClinicalPolicyRegistryError(manifest.reason);
  }
  if (manifest.ready !== true || manifest.registryKey !== 'mgd_plan_generation'
      || manifest.locale !== 'fr' || !SHA256_RE.test(manifest.manifestSha256 || '')) {
    throw new ClinicalPolicyRegistryError('clinical_policy_response_invalid');
  }
  if (manifest.validUntil && new Date(manifest.validUntil).getTime() <= Date.now()) {
    throw new ClinicalPolicyRegistryError('clinical_policy_expired');
  }
  return manifest;
}

export function _resetClinicalPolicyRegistryCache() {
  registryState = null;
}

/** Lit le texte d'autorisation produit depuis le serveur. Aucun texte local de repli. */
export async function readMgdProductAuthorization(supabaseClient) {
  if (!supabaseClient || typeof supabaseClient.rpc !== 'function') {
    return unavailableProductAuthorization();
  }
  try {
    const { data, error } = await supabaseClient.rpc('lire_autorisation_produit_mgd');
    if (error) return unavailableProductAuthorization();
    return normalizeProductAuthorization(data)
      || unavailableProductAuthorization('product_authorization_response_invalid');
  } catch {
    return unavailableProductAuthorization();
  }
}

/** Enregistre une autorisation ou une révocation explicite, liée au texte affiché. */
export async function decideMgdProductAuthorization(supabaseClient, {
  expectedStatementHash, decision, provenance,
} = {}) {
  if (!supabaseClient || typeof supabaseClient.rpc !== 'function'
      || !SHA256_RE.test(expectedStatementHash || '')
      || !['authorize', 'revoke'].includes(decision)
      || !isPlainObject(provenance) || Object.keys(provenance).length === 0) {
    return { ok: false, error: 'Décision d’autorisation produit invalide.' };
  }
  try {
    const { data, error } = await supabaseClient.rpc('decider_autorisation_produit_mgd', {
      p_expected_statement_hash: expectedStatementHash,
      p_decision: decision,
      p_provenance: provenance,
    });
    const normalized = error ? null : normalizeProductAuthorization(data);
    const expectedActive = decision === 'authorize';
    if (!normalized || normalized.active !== expectedActive) {
      return { ok: false, error: 'L’autorisation produit n’a pas pu être enregistrée.' };
    }
    return { ok: true, data: normalized };
  } catch {
    return { ok: false, error: 'L’autorisation produit n’a pas pu être enregistrée.' };
  }
}

/** Revue explicite d'une ligne du registre, protégée par l'empreinte affichée. */
export async function reviewMgdClinicalPolicyItem(supabaseClient, {
  componentKey, itemKey, expectedContentSha256, decision, correctedPayload = null, provenance,
} = {}) {
  const correctionValid = decision === 'correct'
    ? isPlainObject(correctedPayload)
    : correctedPayload === null;
  if (!supabaseClient || typeof supabaseClient.rpc !== 'function'
      || typeof componentKey !== 'string' || !componentKey.trim()
      || typeof itemKey !== 'string' || !itemKey.trim()
      || !SHA256_RE.test(expectedContentSha256 || '')
      || !['accept', 'correct', 'refuse'].includes(decision)
      || !correctionValid
      || !isPlainObject(provenance) || Object.keys(provenance).length === 0) {
    return { ok: false, error: 'Décision de ligne clinique invalide.' };
  }
  try {
    const { data, error } = await supabaseClient.rpc('decider_ligne_registre_clinique_mgd', {
      p_component_key: componentKey,
      p_item_key: itemKey,
      p_expected_content_sha256: expectedContentSha256,
      p_decision: decision,
      p_corrected_payload: decision === 'correct' ? correctedPayload : null,
      p_provenance: provenance,
    });
    const fields = ['componentKey', 'contentSha256', 'decidedAt', 'decision', 'decisionId',
      'itemKey', 'manifestInvalidated', 'version'];
    if (error || !exactObject(data, fields) || data.version !== 1
        || data.componentKey !== componentKey || data.itemKey !== itemKey
        || data.decision !== decision || !SHA256_RE.test(data.contentSha256 || '')
        || !UUID_RE.test(data.decisionId || '') || !validIsoTimestamp(data.decidedAt)
        || data.manifestInvalidated !== true) {
      return { ok: false, error: 'La décision de ligne clinique n’a pas pu être enregistrée.' };
    }
    _resetClinicalPolicyRegistryCache();
    return { ok: true, data: immutableJson(data) };
  } catch {
    return { ok: false, error: 'La décision de ligne clinique n’a pas pu être enregistrée.' };
  }
}

/** Confirme une règle exactement telle qu'affichée ; l'empreinte évite les races. */
export async function confirmMgdClinicalPolicy(supabaseClient, confirmation, provenance) {
  if (!supabaseClient || typeof supabaseClient.rpc !== 'function'
      || !isPlainObject(confirmation)
      || !['clearance_policy', 'component_roster'].includes(confirmation.target_type)
      || typeof confirmation.target_key !== 'string' || !confirmation.target_key
      || !SHA256_RE.test(confirmation.content_sha256 || '')
      || !isPlainObject(provenance) || Object.keys(provenance).length === 0) {
    return { ok: false, error: 'Confirmation clinique invalide.' };
  }
  try {
    const isComponent = confirmation.target_type === 'component_roster';
    const { data, error } = await supabaseClient.rpc(
      isComponent ? 'confirmer_composant_registre_clinique_mgd' : 'confirmer_politique_clairance_mgd',
      isComponent ? {
        p_component_key: confirmation.target_key,
        p_expected_content_sha256: confirmation.content_sha256,
        p_confirmation_provenance: provenance,
      } : {
        p_policy_key: confirmation.target_key,
        p_expected_parameters_sha256: confirmation.content_sha256,
        p_confirmation_provenance: provenance,
      },
    );
    if (error || data?.confirmed !== true) {
      return { ok: false, error: 'La règle clinique n’a pas pu être confirmée.' };
    }
    _resetClinicalPolicyRegistryCache();
    return { ok: true, data };
  } catch {
    return { ok: false, error: 'La règle clinique n’a pas pu être confirmée.' };
  }
}

/** Scellement distinct, après confirmation de toutes les règles en attente. */
export async function sealMgdClinicalPolicyRegistry(supabaseClient, {
  expectedManifestSha256, validUntil = null, provenance,
} = {}) {
  if (!supabaseClient || typeof supabaseClient.rpc !== 'function'
      || !SHA256_RE.test(expectedManifestSha256 || '')
      || !isPlainObject(provenance) || Object.keys(provenance).length === 0) {
    return { ok: false, error: 'Demande de scellement clinique invalide.' };
  }
  try {
    const { data, error } = await supabaseClient.rpc('sceller_registre_clinique_mgd', {
      p_valid_until: validUntil,
      p_expected_current_manifest_sha256: expectedManifestSha256,
      p_provenance: provenance,
    });
    if (error || !isPlainObject(data) || data.ready !== true
        || !Number.isInteger(data.policy_version) || data.policy_version <= 0
        || !SHA256_RE.test(data.manifest_sha256 || '')
        || data.predecessor_manifest_sha256 !== expectedManifestSha256
        || typeof data.idempotent !== 'boolean'
        || !isPlainObject(data.component_hashes)
        || !isPlainObject(data.component_rosters)) {
      return { ok: false, error: 'Le registre clinique n’a pas pu être scellé.' };
    }
    _resetClinicalPolicyRegistryCache();
    return { ok: true, data };
  } catch {
    return { ok: false, error: 'Le registre clinique n’a pas pu être scellé.' };
  }
}
